/**
 * Retention and freeze, for the Audit Logs screen.
 *
 * Kept deliberately small. This is an operational control, not a second audit
 * product: what are we keeping, is anything frozen, and who froze it.
 *
 * Two things it must communicate, because getting them wrong is expensive:
 *
 *   1. Security events are never removed by retention, at any setting. An
 *      operator who believes a shorter window will eventually clear login and
 *      impersonation history is wrong, and would find out at the worst moment.
 *   2. A freeze outranks everything. While one is on, nothing is purged.
 *
 * Both are stated on the screen rather than left to be inferred from a number.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { PermissionGate } from '../components';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { ShieldCheck, Snowflake, Loader2, AlertTriangle, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASS_COPY = {
  security: {
    label: 'Security',
    tone: 'bg-rose-50 text-rose-700 border-rose-200',
    blurb: 'Sign-ins, refusals, impersonation, deletions and every change to '
      + 'this policy. Never removed by retention, at any setting.',
  },
  administrative: {
    label: 'Administrative',
    tone: 'bg-amber-50 text-amber-800 border-amber-200',
    blurb: 'Plans, licences, releases and entitlements — the records a billing '
      + 'question reaches for. Kept for at least 24 months whatever the policy says.',
  },
  operational: {
    label: 'Operational',
    tone: 'bg-slate-100 text-slate-700 border-slate-200',
    blurb: 'Routine events explicitly classified as such. These are the only '
      + 'ones the retention window removes.',
  },
};

const RetentionPanel = () => {
  const { getAdminToken } = useAdminAuth();
  const auth = useCallback(
    () => ({ Authorization: `Bearer ${getAdminToken()}`, 'Content-Type': 'application/json' }),
    [getAdminToken],
  );

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [freezeOpen, setFreezeOpen] = useState(false);
  const [freezeReason, setFreezeReason] = useState('');
  const [unfreezeOpen, setUnfreezeOpen] = useState(false);
  const [unfreezeReason, setUnfreezeReason] = useState('');
  const [months, setMonths] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/audit-logs/retention`, { headers: auth() });
      if (!res.ok) throw new Error(`Could not load the retention policy (${res.status})`);
      const data = await res.json();
      setState(data);
      setMonths(String(data.policy.retention_months));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const call = async (path, body, method = 'POST') => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/audit-logs/${path}`, {
        method, headers: auth(), body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Request failed (${res.status})`);
      setError(null);
      await load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="flex items-center gap-2 py-6 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading retention policy…
      </CardContent></Card>
    );
  }
  if (!state) {
    return (
      <Card><CardContent className="py-6 text-sm text-rose-700">
        {error || 'The retention policy could not be read.'}
      </CardContent></Card>
    );
  }

  const { policy, counts_by_class: counts, eligible_for_purge: eligible, total_rows: total } = state;
  const frozen = policy.frozen;

  return (
    <Card data-testid="audit-retention-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-500" /> Retention
            </CardTitle>
            <CardDescription>
              What this log keeps, and what it is allowed to forget.
            </CardDescription>
          </div>
          {frozen && (
            <Badge className="bg-sky-100 text-sky-800 border-sky-200 gap-1" data-testid="audit-frozen-badge">
              <Snowflake className="h-3 w-3" /> Frozen
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <p className="flex items-start gap-2 text-sm text-rose-700" data-testid="audit-retention-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </p>
        )}

        {frozen && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm" data-testid="audit-freeze-detail">
            <p className="font-medium text-sky-900">
              Purging is stopped. Nothing is removed at any age while this is on.
            </p>
            <p className="mt-1 text-sky-800">
              Frozen by <strong>{policy.frozen_by}</strong>
              {policy.frozen_at && <> on {new Date(policy.frozen_at).toLocaleString()}</>}.
            </p>
            <p className="mt-1 text-sky-800">Reason: “{policy.freeze_reason}”</p>
          </div>
        )}

        {/* The three classes, said plainly. */}
        <div className="grid gap-2 sm:grid-cols-3">
          {['security', 'administrative', 'operational'].map((key) => (
            <div key={key} className={`rounded-lg border p-3 ${CLASS_COPY[key].tone}`}>
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium">{CLASS_COPY[key].label}</span>
                <span className="tabular-nums text-sm">{counts?.[key] ?? 0}</span>
              </div>
              <p className="mt-1 text-xs leading-snug opacity-90">{CLASS_COPY[key].blurb}</p>
              {key !== 'security' && (
                <p className="mt-1 text-xs opacity-75">
                  {eligible?.[key] ?? 0} currently past the window
                </p>
              )}
              {key === 'security' && (
                <p className="mt-1 flex items-center gap-1 text-xs font-medium">
                  <Lock className="h-3 w-3" /> Protected — cannot be aged out
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t pt-4">
          <div>
            <Label className="text-xs">Keep ordinary history for</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="number" min="1" max="240" className="w-24"
                value={months} onChange={(e) => setMonths(e.target.value)}
                data-testid="audit-retention-months"
              />
              <span className="text-sm text-slate-600">months</span>
              <PermissionGate permission="audit:purge">
                <Button
                  size="sm" variant="outline" disabled={busy || String(policy.retention_months) === months}
                  onClick={() => call('retention', { months: Number(months) }, 'PATCH')}
                  data-testid="audit-retention-save"
                >
                  Save
                </Button>
              </PermissionGate>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Administrative history is kept for at least {policy.administrative_floor_months} months
              regardless. Security events are never removed.
            </p>
          </div>

          <div className="ml-auto flex gap-2">
            {frozen ? (
              <PermissionGate permission="audit:purge">
                <Button size="sm" variant="outline" onClick={() => setUnfreezeOpen(true)}
                  data-testid="audit-unfreeze-btn">
                  Unfreeze
                </Button>
              </PermissionGate>
            ) : (
              <PermissionGate permission="audit:purge">
                <Button size="sm" variant="outline" onClick={() => setFreezeOpen(true)}
                  data-testid="audit-freeze-btn" className="gap-1">
                  <Snowflake className="h-4 w-4" /> Freeze
                </Button>
              </PermissionGate>
            )}
          </div>
        </div>

        <p className="text-xs text-slate-500">
          {total} events recorded. {frozen ? 'Nothing is eligible for removal while frozen.'
            : `${state.would_delete} currently eligible for removal.`}
        </p>
      </CardContent>

      <Dialog open={freezeOpen} onOpenChange={setFreezeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Freeze the audit log</DialogTitle>
            <DialogDescription>
              Nothing will be removed at any age until it is unfrozen. The reason is
              recorded and shown to whoever looks next — a freeze with no stated
              cause cannot be relied on later.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason <span className="text-rose-600">*</span></Label>
            <Textarea
              rows={3} value={freezeReason} onChange={(e) => setFreezeReason(e.target.value)}
              placeholder="e.g. security review of March access"
              data-testid="audit-freeze-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFreezeOpen(false)}>Cancel</Button>
            <Button
              disabled={busy || !freezeReason.trim()}
              onClick={async () => {
                if (await call('freeze', { frozen: true, reason: freezeReason.trim() })) {
                  setFreezeOpen(false); setFreezeReason('');
                }
              }}
              data-testid="audit-freeze-confirm"
            >
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Freeze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unfreezeOpen} onOpenChange={setUnfreezeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unfreeze the audit log</DialogTitle>
            <DialogDescription>
              Retention resumes. Ordinary history past the window becomes eligible for
              removal again; security events remain protected either way. This is
              recorded as an audit event.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason <span className="text-slate-400">(optional)</span></Label>
            <Textarea
              rows={2} value={unfreezeReason} onChange={(e) => setUnfreezeReason(e.target.value)}
              placeholder="e.g. review complete"
              data-testid="audit-unfreeze-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnfreezeOpen(false)}>Cancel</Button>
            <Button
              disabled={busy}
              onClick={async () => {
                if (await call('freeze', { frozen: false, reason: unfreezeReason.trim() || null })) {
                  setUnfreezeOpen(false); setUnfreezeReason('');
                }
              }}
              data-testid="audit-unfreeze-confirm"
            >
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Unfreeze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RetentionPanel;
